/**
 * Dashboards, as metadata.
 *
 * A dashboard is a list of widgets; each widget names a computation the server
 * knows how to run. Like reports, the numbers are code and the layout is data —
 * and like reports, nothing loads until the filters are applied (docs/03).
 */
export type WidgetKind = 'kpi' | 'split' | 'rank' | 'series' | 'gauge'

export interface WidgetDef {
  key: string
  kind: WidgetKind
  title: string
  subtitle?: string
  /** Half width by default; a series usually wants the full row. */
  span?: 1 | 2
  /** How the value is written. */
  format?: 'money' | 'qty' | 'count' | 'pct'
  /**
   * Which report filter a row of this widget sets when it is opened
   * (docs/03 — `Show details`).
   *
   * A bar naming a project opens that project's financials; one naming a
   * supplier opens what has been bought from them. Without it a dashboard is a
   * picture rather than a way in.
   */
  drillTo?: 'projectId' | 'supplierId' | 'jobTypeId'
}

export interface DashboardFilterDef {
  key: string
  label: string
  control: 'dateRange' | 'lookup' | 'checkbox'
  required?: boolean
  lookup?: { entity: string }
}

export interface DashboardDef {
  key: string
  route: string
  label: string
  blurb: string
  filters: readonly DashboardFilterDef[]
  widgets: readonly WidgetDef[]
}

export const defineDashboard = <const T extends DashboardDef>(def: T): T => def

export const projectOverview = defineDashboard({
  key: 'project_overview', route: 'projects_dashboard',
  label: 'Project overview',
  blurb: 'Open, billed and unbilled value across every project, with cost against it.',
  filters: [
    { key: 'period', label: 'Period', control: 'dateRange', required: true },
    { key: 'jobTypeId', label: 'Job type', control: 'lookup', lookup: { entity: 'job_type' } },
  ],
  widgets: [
    { key: 'open_order_value', kind: 'split', title: 'Open order value', subtitle: 'By job type', format: 'money', drillTo: 'jobTypeId' },
    { key: 'unbilled_value', kind: 'split', title: 'Unbilled value', subtitle: 'Work done but not yet invoiced', format: 'money' },
    { key: 'top_projects_open', kind: 'rank', title: 'Top projects by open order value', format: 'money', drillTo: 'projectId' },
    { key: 'top_projects_unbilled', kind: 'rank', title: 'Top projects by unbilled value', format: 'money', drillTo: 'projectId' },
    { key: 'billed_vs_cost', kind: 'series', title: 'Billing against cost, month by month', span: 2, format: 'money' },
    { key: 'cost_by_type', kind: 'split', title: 'Cost incurred by cost type', format: 'money' },
    { key: 'project_counts', kind: 'kpi', title: 'Projects', format: 'count' },
    // What has been won month by month, which is the question a management
    // meeting opens with and a single count of live projects cannot answer.
    { key: 'new_project_value', kind: 'series', title: 'New project value, month by month', span: 2, format: 'money' },
  ],
} as const)

export const projectsHealth = defineDashboard({
  key: 'projects_health', route: 'project_health_dashboard',
  label: 'Projects health',
  blurb: 'Every live project with its cost and revenue position, worst first.',
  filters: [],
  widgets: [
    { key: 'project_health', kind: 'rank', title: 'Projects by margin', span: 2, format: 'money', drillTo: 'projectId' },
    // The calendar half of health. A job can be making money and still be
    // three months late, and only one of those shows up in a margin.
    { key: 'schedule_slippage', kind: 'rank', title: 'Projects by days late', format: 'count' },
    { key: 'overdue_tasks', kind: 'kpi', title: 'Work past its date', format: 'count' },
  ],
} as const)

export const projectPerformance = defineDashboard({
  key: 'project_performance', route: 'project_performance_dashboard',
  label: 'Project performance',
  blurb: 'One project in depth — progress, cost and revenue against what was promised.',
  filters: [{ key: 'projectId', label: 'Project', control: 'lookup', required: true, lookup: { entity: 'project' } }],
  widgets: [
    /*
      docs/03 §3 — `Planned Start`, `Planned Finish`, `Forecast Finish`,
      `Delay (Days)`, and the elapsed / remaining / additional legend under
      them. The Schedule screen has these figures; a project dashboard that
      talks about cost and revenue and says nothing about dates is answering
      half the question somebody came with.
    */
    { key: 'project_timeline', kind: 'kpi', title: 'Project timeline', span: 2, format: 'count' },
    { key: 'contract_value', kind: 'kpi', title: 'Contract value', format: 'money' },
    { key: 'billing_performance', kind: 'gauge', title: 'Billing performance', format: 'money' },
    { key: 'cost_performance', kind: 'gauge', title: 'Cost performance', format: 'money' },
    { key: 'cost_by_type', kind: 'split', title: 'Cost by type', format: 'money' },
    { key: 'schedule_performance', kind: 'gauge', title: 'Schedule performance', format: 'count' },
  ],
} as const)

export const purchaseOverview = defineDashboard({
  key: 'purchase_overview', route: 'purchase_dashboard',
  label: 'Purchase overview',
  blurb: 'Purchasing and subcontracting across the business.',
  filters: [
    { key: 'period', label: 'Period', control: 'dateRange', required: true },
    { key: 'supplierId', label: 'Supplier', control: 'lookup', lookup: { entity: 'supplier' } },
  ],
  widgets: [
    { key: 'open_orders_value', kind: 'split', title: 'Open order value', subtitle: 'Purchase against subcontract', format: 'money' },
    { key: 'open_orders_count', kind: 'split', title: 'Open orders', subtitle: 'How many, purchase against subcontract', format: 'count' },
    { key: 'purchase_by_month', kind: 'series', title: 'Purchase value, month by month', span: 2, format: 'money' },
    { key: 'invoices_received_by_month', kind: 'series', title: 'Purchase invoices received, month by month', span: 2, format: 'money' },
    { key: 'order_count_by_month', kind: 'series', title: 'Order count, month by month', span: 2, format: 'count' },
    /*
      Savings is a signed number and is often negative — the demo tenant's own
      total is minus twenty-nine thousand. A widget that could only go up would
      be the wrong shape for the thing being measured.
    */
    { key: 'savings_by_month', kind: 'series', title: 'Purchase savings, month by month', span: 2, format: 'money' },
    { key: 'top_buyers', kind: 'rank', title: 'Purchase executives by savings', format: 'money' },
    { key: 'top_suppliers', kind: 'rank', title: 'Top suppliers by value', format: 'money', drillTo: 'supplierId' },
    { key: 'top_subcontractors', kind: 'rank', title: 'Top subcontractors by value', format: 'money', drillTo: 'supplierId' },
    { key: 'top_categories', kind: 'rank', title: 'Top material categories', format: 'money' },
  ],
} as const)

export const DASHBOARD_DEFS = [
  projectOverview, projectsHealth, projectPerformance, purchaseOverview,
] as const satisfies readonly DashboardDef[]

const byKey = new Map<string, DashboardDef>(DASHBOARD_DEFS.map((d) => [d.key, d]))
const byRoute = new Map<string, DashboardDef>(DASHBOARD_DEFS.map((d) => [d.route, d]))
export const getDashboardDef = (key: string): DashboardDef | undefined => byKey.get(key)
export const getDashboardDefByRoute = (route: string): DashboardDef | undefined => byRoute.get(route)
